import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { Button, Segment } from 'semantic-ui-react'
import { ContentHeader } from '../../../../../components/Header/ContentHeader'
import { RootState } from '../../../../../store/rootState'
import { IRouteParams } from '../../../../../types/routeParams'
import { IUserDetail } from '../../../../../types/user'
import { checkAdminStatus, checkRole } from '../../../../../utils/checkRole'
import { currencyFormat } from '../../../../../utils/currencyFormat'
import * as CostsActions from '../../../actions/directAndPassTroughCosts'
import { getDirectCostsSelector, getOfferTotalWidgetSelector } from '../../../selectors/directAndPassTroughCostsSelector'
import { ICostWidgetTotal, IDirectCost } from '../../../types/directAndPassTroughCosts'
import { IProject } from '../../../types/project'

interface StateProps {
  project: IProject
  directCosts: IDirectCost[]
  offerTotalWidgetCalculation: ICostWidgetTotal
  user: IUserDetail | null
  activeBudgetTab: string
}

interface DispatchProps {
  getCostFilters: (id: string | number) => void
}

type Props = StateProps & DispatchProps

const ServiceProviderBudgetHeader = (props: Props): JSX.Element => {

  const {
    project,
    directCosts,
    offerTotalWidgetCalculation,
    user,
    activeBudgetTab,
    getCostFilters
  } = props

  const param: IRouteParams = useParams() as IRouteParams

  const _history = useHistory()

  useEffect(() => {
    getCostFilters(param.id)
  }, [getCostFilters, param.id])

  const calcTotalDirectCosts = (costValues: IDirectCost[]): number => {
    const numberList = costValues?.map(a => a.budget ? a.budget : 0)
    const calc = numberList?.reduce((a, b) => (a ? a : 0) + (b ? b : 0), 0)
    return calc ? calc : 0
  }

  const showOffer = (): void => {
    _history.replace(`/projects/${project.id}/budget/project-offer`)
  }

  const renderSegmentContent = (): React.JSX.Element => {
    const projectedProfit: string | number = offerTotalWidgetCalculation.projected_profit ? offerTotalWidgetCalculation.projected_profit.toFixed(2) : 0
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '2rem 0', flexDirection: 'row' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row' }}>
            <p style={{ color: '#172533', fontSize: '.86rem', fontWeight: 'bold' }}>Direct Costs</p>
            <div style={{ display: 'flex', float: 'left', color: '#172533', fontWeight: 'bold', marginLeft: '.5rem' }}>
              <p style={{ margin: 0 }}>{currencyFormat(calcTotalDirectCosts(directCosts))[0]} {currencyFormat(calcTotalDirectCosts(directCosts)).split(',')[0].replace(currencyFormat(calcTotalDirectCosts(directCosts))[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotalDirectCosts(directCosts)).split(',')[1]}</p>
            </div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row' }}>
            <p style={{ color: '#172533', fontSize: '.86rem', fontWeight: 'bold' }}>Projected Profit:</p>
            <div style={{ display: 'flex', float: 'left', color: '#172533', fontWeight: 'bold', marginLeft: '.5rem' }}>
              <p style={{ color: +projectedProfit > 0 ? '#00DC44' : '#f92323', margin: 0 }}>{projectedProfit}%</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderTotalOffer = (): JSX.Element | null => {
    if (user && checkRole(user, [18])) {
      return (
        <Button
          className='button-add-item'
          inverted
          size='small'
          color='teal'
          onClick={() => showOffer()}
          content='Show Offer'
          style={{ height: '2.2rem', padding: '0rem', color: '#08D6A1', marginTop: '.5rem', borderRadius: '.34rem', width: '7.7rem', backgroundColor: 'transparent' }}
        />
      )
    }
    return null
  }

  const budgetWidget = (): JSX.Element => {
    if (activeBudgetTab !== 'external costs' && user && (checkRole(user, [16]) || checkAdminStatus(user, project))) {
      const totalDirectCosts = offerTotalWidgetCalculation.direct_cost_total
      const totalPassCosts = offerTotalWidgetCalculation.pass_trough_total
      return (
        <Segment style={{ margin: 0, border: 'none', borderRadius: '.57rem', boxShadow: '0 0 25px -5px #15151511', width: 'auto', minWidth: '25rem', height: '8.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <p style={{ color: '#172533', fontWeight: 'bold', margin: 0 }}>Project Offer Total</p>
              <div style={{ display: 'flex', float: 'left', color: '#08D6A1', fontWeight: 'bold', fontSize: '1.7rem' }}>
                <p style={{ margin: 0 }}>{currencyFormat(totalDirectCosts + totalPassCosts)[0]} {currencyFormat(totalDirectCosts + totalPassCosts).split(',')[0].replace(currencyFormat(totalDirectCosts + totalPassCosts)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(totalDirectCosts + totalPassCosts).split(',')[1]}</p>
              </div>
            </div>
            {renderTotalOffer()}
          </div>
          <hr style={{ width: '24.9rem', margin: '.5rem -1rem -1.5rem', opacity: .1 }} />
          {renderSegmentContent()}
        </Segment>
      )
    }
    return <div style={{ height: '8.2rem' }}></div>
  }

  return (
    <React.Fragment>
      <ContentHeader
        id='projectActivitiesHeader'
        title={project.name}
        color='#08D6A1'
        backLocation='/projects'
        backTitle={project.type === 'internal' ? 'Expenditures' : 'Budget'}
        subtitle={project.customer?.name ?? project.category}
        rightItemsFirst={budgetWidget()}
        column={true}
      />
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    project: state.projectsState.project,
    directCosts: getDirectCostsSelector(state),
    offerTotalWidgetCalculation: getOfferTotalWidgetSelector(state),
    user: state.authState.user,
    activeBudgetTab: state.projectsState.activeSPTBudgetTab,
  }
}

const mapDispatchToProps = (dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType): DispatchProps => {
  return {
    getCostFilters: id => dispatch(CostsActions.getCostFilters(id)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ServiceProviderBudgetHeader)
